import React from 'react';
import { useTranslation } from 'localization';
import { AuditEntry } from 'types/channel';
import './AuditEntryMolecule.scss';

export interface AuditEntryMoleculeProps {
  entry: AuditEntry;
  className?: string;
}

function formatTimestamp(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return '';
  }
}

const AuditEntryMolecule: React.FC<AuditEntryMoleculeProps> = ({
  entry,
  className = '',
}) => {
  const { t } = useTranslation();

  const actionLabel = t(`marketplace.itemDetail.audit.action.${entry.action}`);
  const relationLabel = t(
    `marketplace.itemDetail.audit.relation.${entry.relation}`
  );
  const targetLabel =
    entry.targetDisplayName ||
    entry.targetIdentityId ||
    entry.targetRoleId ||
    '';
  const performerLabel = entry.performedByDisplayName || entry.performedBy;
  const timestamp = formatTimestamp(entry.performedAt);

  const iconClass =
    entry.action === 'grant'
      ? 'pi pi-check-circle marketplace-audit-entry-molecule__icon--grant'
      : 'pi pi-times-circle marketplace-audit-entry-molecule__icon--revoke';

  const classes = [
    'marketplace-audit-entry-molecule',
    `marketplace-audit-entry-molecule--${entry.action}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li className={classes} data-testid={`audit-entry-${entry.id}`}>
      <span
        className={`marketplace-audit-entry-molecule__icon ${iconClass}`}
        aria-hidden="true"
      />
      <div className="marketplace-audit-entry-molecule__body">
        <p className="marketplace-audit-entry-molecule__summary">
          <span className="marketplace-audit-entry-molecule__action">
            {actionLabel}
          </span>{' '}
          <span className="marketplace-audit-entry-molecule__relation">
            {relationLabel}
          </span>
          {targetLabel && (
            <span
              className="marketplace-audit-entry-molecule__target"
              data-testid="audit-entry-target"
            >
              {targetLabel}
            </span>
          )}
        </p>
        <p className="marketplace-audit-entry-molecule__meta">
          <span
            className="marketplace-audit-entry-molecule__performer"
            data-testid="audit-entry-performer"
          >
            {performerLabel}
          </span>
          {timestamp && (
            <time
              className="marketplace-audit-entry-molecule__timestamp"
              dateTime={entry.performedAt}
              data-testid="audit-entry-timestamp"
            >
              {timestamp}
            </time>
          )}
        </p>
      </div>
    </li>
  );
};

export default AuditEntryMolecule;
